import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from '../styles/Toast.module.css';

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const IconError = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
  </svg>
);

const IconInfo = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
  </svg>
);

const IconWarning = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const severityConfig = {
  info: {
    className: styles.info,
    Icon: IconInfo,
    label: 'League update',
  },
  success: {
    className: styles.success,
    Icon: IconCheck,
    label: 'Confirmed',
  },
  error: {
    className: styles.error,
    Icon: IconError,
    label: 'Something went wrong',
  },
  warning: {
    className: styles.warning,
    Icon: IconWarning,
    label: 'Heads up',
  },
};

const Toast = ({ message, severity = 'info', duration = 5000, onClose }) => {
  const config = severityConfig[severity] || severityConfig.info;
  const { className, Icon, label } = config;

  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose, message]);

  return (
    <div
      className={`${styles.toast} ${className}`}
      role="alert"
      aria-live="polite"
      style={{ '--toast-duration': `${duration}ms` }}
    >
      <div className={styles.content}>
        <div className={styles.iconWrap} aria-hidden="true">
          <Icon />
        </div>
        <div className={styles.body}>
          <span className={styles.label}>{label}</span>
          <p className={styles.message}>{message}</p>
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Dismiss notification"
        >
          <IconClose />
        </button>
      </div>
      <div className={styles.progressTrack} aria-hidden="true">
        <div className={styles.progressBar} />
      </div>
    </div>
  );
};

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['info', 'success', 'error', 'warning']),
  duration: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

export default Toast;
